import React, { useEffect, useState } from "react";
import { app } from "@microsoft/teams-js";
import { setLocale } from "@/locales";
import { isLocaleMatchingCache } from "@/core/utils/languageCache";
import CONFIGS from "@/core/utils/configs";
import { useTranslation } from "react-i18next";
import { useTeamsContext } from "@/hooks/useTeamsContext";
import IframeShell from "@/components/IframeShell";
import { Loader } from "@/components";
import { useStore } from "@/store/store";
import { useEnvironmentStore } from "@/store/useEnvironmentStore";

const IframeRoutes: React.FC = () => {
  const { t } = useTranslation();
  const updateUsername = useStore((state) => state.updateUsername);
  const companyId = useStore((state) => state.config.companyId);
  const companyApiDomain = useStore((state) => state.config.api_end_point);
  const environmentKey = useEnvironmentStore(
    (state) => `${state.env}:${state.reviewAppBranchName}`,
  );
  const [{ inTeams }] = useTeamsContext();
  const [languageInitialized, setLanguageInitialized] = useState(false);
  const [theme, setTheme] = useState<string | undefined>();
  const [teamsClientType, setTeamsClientType] = useState<string | undefined>();

  useEffect(() => {
    if (inTeams === false && CONFIGS.TEST_UPN) {
      updateUsername(CONFIGS.TEST_UPN);
      setLanguageInitialized(true);
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!languageInitialized) {
        setLanguageInitialized(true);
      }
    }, 2000);

    app
      .getContext()
      .then((context: app.Context) => {
        clearTimeout(timeoutId);

        if (context.app.locale) {
          if (!isLocaleMatchingCache(context.app.locale)) {
            setLocale(context.app.locale, "culture");
          }
        }
        setLanguageInitialized(true);
        setTheme(context.app.theme);
        setTeamsClientType(context.app.host?.clientType);

        const username = context?.user?.loginHint ?? "";
        updateUsername(username);
      })
      .catch(() => {
        clearTimeout(timeoutId);
        setLanguageInitialized(true);
      });

    return () => clearTimeout(timeoutId);
  }, [inTeams, updateUsername, languageInitialized]);

  if (!languageInitialized) {
    return <Loader label={t("Initializing...")} />;
  }

  return (
    <IframeShell
      key={`${environmentKey}:${companyApiDomain}:${companyId}`}
      theme={theme}
      teamsClientType={teamsClientType}
    />
  );
};

export default IframeRoutes;
